'use client';

import React from 'react';
import { X, Calendar, TrendingUp, Target, AlertTriangle, BarChart2, Settings2 } from 'lucide-react';
import { BacktestRun } from '../utils/backtestStorage';

interface BacktestRunDetailModalProps {
  run: BacktestRun;
  onClose: () => void;
}

export default function BacktestRunDetailModal({ run, onClose }: BacktestRunDetailModalProps) {
  const { params, results } = run;
  const isProfit = results.netReturn >= 0;

  const values = results.equityCurve.map((d) => d.equity);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const w = 400; const h = 100;
  const pts = values.map((v, i) => {
    const x = (i / (values.length - 1)) * w;
    const y = h - ((v - min) / range) * (h - 8) - 4;
    return `${x},${y}`;
  });
  const fillPts = `${pts.join(' ')} ${w},${h} 0,${h}`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm animate-fade-in">
      <div className="bg-card border border-border rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl">
        {/* Header */}
        <div className="flex items-start justify-between px-6 py-4 border-b border-border sticky top-0 bg-card z-10">
          <div>
            <h2 className="text-base font-bold text-foreground">{run.name}</h2>
            <div className="flex items-center gap-2 mt-0.5 text-xs text-muted-foreground">
              <Calendar size={11} />
              <span>Saved {new Date(run.savedAt).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors">
            <X size={16} />
          </button>
        </div>

        <div className="px-6 py-5 space-y-6">
          {/* KPI Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { label: 'Win Rate', value: `${results.winRate}%`, sub: `${results.wins}W / ${results.losses}L`, icon: <Target size={14} />, positive: results.winRate >= 50 },
              { label: 'Profit Factor', value: results.profitFactor.toFixed(2), sub: `$${results.grossProfit.toLocaleString()} / $${results.grossLoss.toLocaleString()}`, icon: <TrendingUp size={14} />, positive: results.profitFactor >= 1.5 },
              { label: 'Net Return', value: `${isProfit ? '+' : ''}$${results.netReturn.toLocaleString()}`, sub: `${isProfit ? '+' : ''}${results.netReturnPct.toFixed(1)}% on $${parseInt(params.initialCapital).toLocaleString()}`, icon: <BarChart2 size={14} />, positive: isProfit },
              { label: 'Max Drawdown', value: `-${results.maxDrawdownPct.toFixed(1)}%`, sub: `-$${Math.abs(results.maxDrawdown).toLocaleString()}`, icon: <AlertTriangle size={14} />, positive: false, warning: true },
            ].map((kpi) => (
              <div key={`detail-kpi-${kpi.label}`} className={`rounded-xl border p-3.5 ${kpi.warning ? 'bg-warning-subtle border-warning/30' : 'bg-muted/30 border-border'}`}>
                <div className={`inline-flex p-1.5 rounded-lg mb-2 ${kpi.warning ? 'bg-warning/10 text-warning' : 'bg-primary/10 text-primary'}`}>
                  {kpi.icon}
                </div>
                <p className="text-xs text-muted-foreground mb-0.5">{kpi.label}</p>
                <p className={`text-lg font-bold font-mono-nums ${kpi.warning ? 'text-warning' : kpi.positive ? 'text-positive' : 'text-negative'}`}>
                  {kpi.value}
                </p>
                <p className="text-xs text-muted-foreground font-mono-nums mt-0.5">{kpi.sub}</p>
              </div>
            ))}
          </div>

          {/* Equity Curve */}
          <div className="bg-muted/20 border border-border rounded-xl p-4">
            <p className="text-xs font-500 text-muted-foreground uppercase tracking-wider mb-3">Equity Curve</p>
            <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-24" preserveAspectRatio="none">
              <defs>
                <linearGradient id="detail-grad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={isProfit ? 'var(--positive)' : 'var(--negative)'} stopOpacity="0.25" />
                  <stop offset="100%" stopColor={isProfit ? 'var(--positive)' : 'var(--negative)'} stopOpacity="0" />
                </linearGradient>
              </defs>
              <polygon points={fillPts} fill="url(#detail-grad)" />
              <polyline points={pts.join(' ')} fill="none" stroke={isProfit ? 'var(--positive)' : 'var(--negative)'} strokeWidth="2" />
            </svg>
            <div className="flex items-center justify-between mt-2 text-xs font-mono-nums text-muted-foreground">
              <span>{results.equityCurve[0]?.month}</span>
              <span className={`font-600 ${isProfit ? 'text-positive' : 'text-negative'}`}>
                {isProfit ? '+' : ''}${results.netReturn.toLocaleString()} ({isProfit ? '+' : ''}{results.netReturnPct.toFixed(1)}%)
              </span>
              <span>{results.equityCurve[results.equityCurve.length - 1]?.month}</span>
            </div>
          </div>

          {/* Parameters */}
          <div className="bg-muted/20 border border-border rounded-xl p-4">
            <div className="flex items-center gap-2 mb-3">
              <Settings2 size={14} className="text-muted-foreground" />
              <p className="text-xs font-500 text-muted-foreground uppercase tracking-wider">Configuration</p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {[
                { label: 'Symbol', value: params.symbol },
                { label: 'Timeframe', value: params.timeframe },
                { label: 'Period', value: `${params.startDate} → ${params.endDate}` },
                { label: 'Initial Capital', value: `$${parseInt(params.initialCapital).toLocaleString()}` },
                { label: 'Risk / Trade', value: `${params.riskPerTrade}%` },
                { label: 'Stop Loss', value: params.stopLossType },
                { label: 'Take Profit', value: params.takeProfitType },
                { label: 'Min Pattern Score', value: `${params.minPatternScore}%` },
                { label: 'Max Concurrent', value: params.maxOpenTrades },
              ].map((p) => (
                <div key={`detail-param-${p.label}`}>
                  <p className="text-xs text-muted-foreground mb-0.5">{p.label}</p>
                  <p className="text-xs font-500 text-foreground font-mono-nums">{p.value}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Chart snapshot */}
          {run.chartSnapshot && (
            <div className="bg-muted/20 border border-border rounded-xl p-4">
              <p className="text-xs font-500 text-muted-foreground uppercase tracking-wider mb-3">Chart Drawing Snapshot</p>
              <img src={run.chartSnapshot} alt="Chart drawing snapshot from backtest run" className="w-full rounded-lg border border-border" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
